import { useRef } from 'react';
import { Save, Loader2, Upload, Trash2 } from 'lucide-react';
import { useEmpresaConfig, Empresa } from '../../../../hooks/useEmpresaConfig';

type LogoTipo = 'folioClaro' | 'folioEscuro' | 'empresaClaro' | 'empresaEscuro';

export function IdentidadeVisual() {
  const { empresa, setEmpresa, isLoading, isSaving, message, saveEmpresa } = useEmpresaConfig();

  const fileInputFolioClaro = useRef<HTMLInputElement>(null);
  const fileInputFolioEscuro = useRef<HTMLInputElement>(null);
  const fileInputEmpresaClaro = useRef<HTMLInputElement>(null);
  const fileInputEmpresaEscuro = useRef<HTMLInputElement>(null);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>, tipo: LogoTipo) => {
    const file = e.target.files?.[0];
    if (!file || !empresa) return;

    if (file.size > 2 * 1024 * 1024) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const updates: Partial<Empresa> = {};
      switch (tipo) {
        case 'folioClaro': updates.logoFolioClaro = reader.result as string; break;
        case 'folioEscuro': updates.logoFolioEscuro = reader.result as string; break;
        case 'empresaClaro': updates.logoEmpresaClaro = reader.result as string; break;
        case 'empresaEscuro': updates.logoEmpresaEscuro = reader.result as string; break;
      }
      setEmpresa({ ...empresa, ...updates });
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = (tipo: LogoTipo) => {
    if (!empresa) return;
    const updates: Partial<Empresa> = {};
    switch (tipo) {
      case 'folioClaro': updates.logoFolioClaro = undefined; break;
      case 'folioEscuro': updates.logoFolioEscuro = undefined; break;
      case 'empresaClaro': updates.logoEmpresaClaro = undefined; break;
      case 'empresaEscuro': updates.logoEmpresaEscuro = undefined; break;
    }
    setEmpresa({ ...empresa, ...updates });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (empresa) saveEmpresa(empresa);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-brand animate-spin" />
      </div>
    );
  }

  if (!empresa) return null;

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.text}
        </div>
      )}

      {/* Logos */}
      <div className="bg-card border rounded-lg p-6">
        <h3 className="font-semibold text-foreground border-b pb-2 mb-4">Logotipos</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Configure as logos do sistema Folio e da sua empresa separadamente. As logos da empresa serão usadas nos documentos PDF gerados.
        </p>

        {/* Logos do Folio (Sistema) */}
        <div className="mb-8">
          <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <span className="px-2 py-0.5 bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 rounded text-xs font-medium">SISTEMA</span>
            Logos do Folio
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h5 className="text-sm font-medium text-foreground mb-3">Modo Claro</h5>
              <div className="flex items-center gap-4">
                <div className="w-28 h-28 border-2 border-dashed border-input rounded-lg flex items-center justify-center overflow-hidden bg-white">
                  {empresa.logoFolioClaro ? (
                    <img src={empresa.logoFolioClaro} alt="Logo Folio Claro" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-muted-foreground text-xs text-center px-2">Sem logo</span>
                  )}
                </div>
                <div className="space-y-2">
                  <input type="file" ref={fileInputFolioClaro} onChange={(e) => handleLogoUpload(e, 'folioClaro')} accept="image/*" className="hidden" />
                  <button type="button" onClick={() => fileInputFolioClaro.current?.click()} className="flex items-center gap-2 px-3 py-1.5 text-sm btn btn-primary">
                    <Upload className="w-3 h-3" /> Enviar
                  </button>
                  {empresa.logoFolioClaro && (
                    <button type="button" onClick={() => removeLogo('folioClaro')} className="flex items-center gap-2 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-500/10">
                      <Trash2 className="w-3 h-3" /> Remover
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Usada na interface com tema claro</p>
            </div>

            <div>
              <h5 className="text-sm font-medium text-foreground mb-3">Modo Escuro</h5>
              <div className="flex items-center gap-4">
                <div className="w-28 h-28 border-2 border-dashed border-input rounded-lg flex items-center justify-center overflow-hidden bg-gray-900">
                  {empresa.logoFolioEscuro ? (
                    <img src={empresa.logoFolioEscuro} alt="Logo Folio Escuro" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-gray-400 text-xs text-center px-2">Sem logo</span>
                  )}
                </div>
                <div className="space-y-2">
                  <input type="file" ref={fileInputFolioEscuro} onChange={(e) => handleLogoUpload(e, 'folioEscuro')} accept="image/*" className="hidden" />
                  <button type="button" onClick={() => fileInputFolioEscuro.current?.click()} className="flex items-center gap-2 px-3 py-1.5 text-sm btn btn-primary">
                    <Upload className="w-3 h-3" /> Enviar
                  </button>
                  {empresa.logoFolioEscuro && (
                    <button type="button" onClick={() => removeLogo('folioEscuro')} className="flex items-center gap-2 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-500/10">
                      <Trash2 className="w-3 h-3" /> Remover
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Usada na interface com tema escuro</p>
            </div>
          </div>
        </div>

        {/* Logos da Empresa (Documentos) */}
        <div className="border-t pt-6">
          <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <span className="px-2 py-0.5 bg-green-500/10 text-green-600 dark:text-green-400 rounded text-xs font-medium">DOCUMENTOS</span>
            Logos da Empresa
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h5 className="text-sm font-medium text-foreground mb-3">Modo Claro</h5>
              <div className="flex items-center gap-4">
                <div className="w-28 h-28 border-2 border-dashed border-input rounded-lg flex items-center justify-center overflow-hidden bg-white">
                  {empresa.logoEmpresaClaro ? (
                    <img src={empresa.logoEmpresaClaro} alt="Logo Empresa Claro" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-muted-foreground text-xs text-center px-2">Sem logo</span>
                  )}
                </div>
                <div className="space-y-2">
                  <input type="file" ref={fileInputEmpresaClaro} onChange={(e) => handleLogoUpload(e, 'empresaClaro')} accept="image/*" className="hidden" />
                  <button type="button" onClick={() => fileInputEmpresaClaro.current?.click()} className="flex items-center gap-2 px-3 py-1.5 text-sm btn btn-primary">
                    <Upload className="w-3 h-3" /> Enviar
                  </button>
                  {empresa.logoEmpresaClaro && (
                    <button type="button" onClick={() => removeLogo('empresaClaro')} className="flex items-center gap-2 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-500/10">
                      <Trash2 className="w-3 h-3" /> Remover
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Usada em PDFs e documentos impressos</p>
            </div>

            <div>
              <h5 className="text-sm font-medium text-foreground mb-3">Modo Escuro</h5>
              <div className="flex items-center gap-4">
                <div className="w-28 h-28 border-2 border-dashed border-input rounded-lg flex items-center justify-center overflow-hidden bg-gray-900">
                  {empresa.logoEmpresaEscuro ? (
                    <img src={empresa.logoEmpresaEscuro} alt="Logo Empresa Escuro" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-gray-400 text-xs text-center px-2">Sem logo</span>
                  )}
                </div>
                <div className="space-y-2">
                  <input type="file" ref={fileInputEmpresaEscuro} onChange={(e) => handleLogoUpload(e, 'empresaEscuro')} accept="image/*" className="hidden" />
                  <button type="button" onClick={() => fileInputEmpresaEscuro.current?.click()} className="flex items-center gap-2 px-3 py-1.5 text-sm btn btn-primary">
                    <Upload className="w-3 h-3" /> Enviar
                  </button>
                  {empresa.logoEmpresaEscuro && (
                    <button type="button" onClick={() => removeLogo('empresaEscuro')} className="flex items-center gap-2 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-500/10">
                      <Trash2 className="w-3 h-3" /> Remover
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Alternativa para fundos escuros (opcional)</p>
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mt-6 p-3 bg-muted/50 rounded-lg">
          💡 <strong>Dica:</strong> PNG com fundo transparente funciona melhor. Máximo 2MB por imagem.
        </p>
      </div>

      <div className="flex justify-end">
        <button type="submit" disabled={isSaving} className="flex items-center gap-2 px-6 py-2 btn btn-primary disabled:opacity-50">
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar Configurações
        </button>
      </div>
    </form>
  );
}
